import { AfterContentInit, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { AttributePresenceInput } from "@spscommerce/ng-utils";
import { onNextTick } from "@spscommerce/utils";
import autobind from "autobind-decorator";

import { SpsTableRowComponent } from "../table-row/sps-table-row.component";
import { SpsTableL1Sorting } from "./l1-sorting";

export class SpsTableL2RowSelection extends SpsTableL1Sorting implements AfterContentInit, OnChanges {
    static readonly props = {
        ...SpsTableL1Sorting.props,
        selection: "Array<number>",
        selectionChange: { event: true, type: "Array<number>" },
        selectable: "boolean",
    };

    @Input() selection: number[] = [];
    @Output() selectionChange = new EventEmitter<number[]>();

    @Input() @AttributePresenceInput() selectable: boolean;

    lastSelectedRow: number;
    partiallySelected = false;
    private _allSelected = false;
    get allSelected(): boolean {
        return this._allSelected;
    }
    set allSelected(value: boolean) {
        this._allSelected = value;
        this.partiallySelected = false;
        this.lastSelectedRow = undefined;
        this.updateSelectionsFromFlag();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (changes.selection && !changes.selection.firstChange) {
            onNextTick(this.updateSelectionsFromInput);
        }
    }

    ngAfterContentInit() {
        super.ngAfterContentInit();

        if (this.selectable) {
            this.body.rows.forEach(this._makeRowSelectable);
            onNextTick(this.updateSelectionsFromInput);
            this.body.rows.changes.subscribe(() => {
                this.body.rows.forEach(this._makeRowSelectable);
            });
        }
    }

    @autobind
    _makeRowSelectable(row: SpsTableRowComponent, index: number) {
        if (!row.selectable) {
            row._makeSelectable();
        }
        row.selectedChange.subscribe((selected: boolean) => {
            if (selected) {
                this.lastSelectedRow = index;
            }
            this.updateSelectionFlags();
            this._emitSelected();
        });
        row.shiftClick.subscribe(() => {
            const rowsArray = this.body.rows.toArray();
            const [low, high] = this.lastSelectedRow < index
                ? [this.lastSelectedRow, index]
                : [index, this.lastSelectedRow];
            for (let i = low; i <= high; i++) {
                rowsArray[i].select(true);
            }
            this.updateSelectionFlags();
        });
    }

    @autobind
    updateSelectionFlags() {
        const selectedCount = this.body.rows.filter(row => row.selected).length;
        this._allSelected = selectedCount === this.body.rows.length;
        this.partiallySelected = selectedCount > 0 && selectedCount < this.body.rows.length;
    }

    updateSelectionsFromFlag() {
        if (this.body && this.body.rows) {
            // https://github.com/angular/angular/issues/29842
            for (const row of (this.body.rows as any as Iterable<SpsTableRowComponent>)) {
                row.select(this.allSelected);
            }
            this._emitSelected();
        }
    }

    @autobind
    updateSelectionsFromInput() {
        if (this.body && this.body.rows) {
            this.selection = this.selection || [];
            // https://github.com/angular/angular/issues/29842
            for (const row of (this.body.rows as any as Iterable<SpsTableRowComponent>)) {
                row.select(false);
            }
            const rowsArray = this.body.rows.toArray();
            for (const index of this.selection) {
                rowsArray[index].select(true);
            }
            this.updateSelectionFlags();
        }
    }

    private _emitSelected() {
        this.selection = this.body.rows.reduce(this._selectedRowIndexReducer, []);
        this.selectionChange.emit(this.selection);
    }

    @autobind
    private _selectedRowIndexReducer(acc: number[], row: SpsTableRowComponent, i: number): number[] {
        if (row.selected) {
            acc.push(i);
        }
        return acc;
    }
}
